import { useCallback, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { parseIds, serializeIds } from "./reference-list.utils";
import { ConfigurationField } from "../../types";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { fetchCockpitWorkflowStatuses } from "@/features/recruitnow/workflow-statuses/api/useCockpitWorkflowStatuses";
import { FilterOption } from "@/types/FilterOption";

// Stable option accessors (kept module-level so AsyncSelect's effects don't re-run each render).
const getOptionValue = (o: FilterOption<string>) => o.value;
const getOptionLabel = (o: FilterOption<string>) => o.label;
const filterByLabel = (o: FilterOption<string>, query: string) =>
  o.label.toLowerCase().includes(query.toLowerCase());

/**
 * Renders a `reference-list` config field: a multi-selection of referenced entities, using the
 * shared `AsyncSelect` (multiple mode). Dispatches on `reference-type` for the data source (currently
 * WorkflowStatuses) and stores the selection as a JSON array string via the generic config-save path.
 */
export function ReferenceListField({
  field,
  disabled,
  clientId,
  moduleName,
}: {
  field: Extract<ConfigurationField, { type: "reference-list" }>;
  disabled?: boolean;
  clientId?: string;
  // The module whose config is being edited — the reference data is served per module.
  moduleName?: string;
}) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const isWorkflowStatuses =
    field.options["reference-type"].toLowerCase() === "workflowstatuses";
  const placeholder = field.placeholder?.startsWith("integrations.")
    ? t(field.placeholder)
    : field.placeholder || "";

  // Only WorkflowStatuses is wired up so far; a schema using this field type with any other
  // reference-type would render an empty list — surface that as a dev signal rather than silently.
  useEffect(() => {
    if (!isWorkflowStatuses) {
      console.warn(
        `ReferenceListField: unsupported reference-type "${field.options["reference-type"]}"; ` +
          `only "WorkflowStatuses" is implemented, rendering an empty list.`
      );
    }
  }, [isWorkflowStatuses, field.options]);

  const selected = parseIds(useWatch({ control, name: field.key }));

  const fetcher = useCallback(async (): Promise<FilterOption<string>[]> => {
    if (!isWorkflowStatuses || !clientId || !moduleName) return [];
    const statuses = await fetchCockpitWorkflowStatuses(clientId, moduleName);
    return statuses.map((s) => ({ value: s.id, label: s.name ?? s.id }));
  }, [isWorkflowStatuses, clientId, moduleName]);

  const commit = (ids: string[]) =>
    setValue(field.key, serializeIds(ids), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

  return (
    <FormField
      control={control}
      name={field.key}
      render={({ fieldState }) => (
        <FormItem>
          <FormLabel>
            {t(field.label)}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </FormLabel>
          <FormControl>
            <AsyncSelect<FilterOption<string>>
              multiple
              preload
              fetcher={fetcher}
              filterFn={filterByLabel}
              renderOption={getOptionLabel}
              getOptionValue={getOptionValue}
              getDisplayValue={getOptionLabel}
              value={selected}
              onChange={commit}
              placeholder={placeholder}
              disabled={disabled}
              width="100%"
              noResultsMessage={t("commonSentences.noOptionsFound")}
              clearAriaLabel={t("commonSentences.clearSelection")}
              aria-invalid={!!fieldState.error}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
